import React, { FC, startTransition, useEffect, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation, useRefetchableFragment } from 'react-relay';
import IfCanAccess from 'src/components/widgets/ifCanAccess/ifCanAccess';
import { UpgradeDialog } from 'src/components/widgets/upgradeDialog/upgradeDialog';
import { backofficeUrl, stringFormat, T, useQueryParams } from 'src/shared/helpers';
import { MarketPlaceProviders } from 'src/shared/hooks/__generated__/useSubscriptionData_subscriptionDataFragment.graphql';
import { Link, PrimaryButton, TextButton } from '../../widgets/buttons';
import { Checkbox } from '../../widgets/checkbox';
import { useDialogManager } from '../../widgets/dialog';
import { NotificationBanner } from '../../widgets/notificationBanner';
import { showNotification } from '../../widgets/notificationToaster';
import { AdvancedSelect } from '../../widgets/select';
import { Spinner } from '../../widgets/spinner';
import { ZTooltip } from '../../widgets/ztooltip';

import { serviceCredits_fragmentServiceCreditQuery } from './__generated__/serviceCredits_fragmentServiceCreditQuery.graphql';
import {
    serviceCreditsBuyServiceCreditsMutation,
    serviceCreditsBuyServiceCreditsMutation$data
} from './__generated__/serviceCreditsBuyServiceCreditsMutation.graphql';
import { serviceCreditsBuyTestOrdersMutation, serviceCreditsBuyTestOrdersMutation$data } from './__generated__/serviceCreditsBuyTestOrdersMutation.graphql';
import { serviceCreditsQuery } from './__generated__/serviceCreditsQuery.graphql';
import { serviceCreditsServiceCredit_fragment$key, ServicesWithCredits } from './__generated__/serviceCreditsServiceCredit_fragment.graphql';

import Loader from 'src/components/widgets/loader';
import { PrimaryButtonWrapper } from 'src/components/widgets/section/sectionFooter';
import { showLoadingAction } from 'src/shared/actions';
import { useAppState } from 'src/shared/appState';
import config from 'src/shared/config';
import { Center, OnlyPaidPlans } from 'src/shared/globals';
import {
    ActionsSection,
    ActivateTrialButton,
    AutoRefillSection,
    CreditsInfoSection,
    CreditsLabel,
    CreditsWarning,
    LinkSuccessPurchasingCreditsMessage,
    LoadingOverlay,
    PackageDiscountBadge,
    PackageOptionLabel,
    PackageOptionPrice,
    PackageOriginalPrice,
    SelectorsRow,
    ServiceCreditsContainer,
    ServiceCreditsContent,
    ServiceCreditsSectionTitle,
    TierSelectionSection,
    TrialBadgeWrapper
} from './serviceCreditsComponents';
import {
    ActivateRemoveContainer,
    AddOnDescription,
    AddOnHeader,
    AddPriceSection,
    BadgeStatusLabel,
    BorderedRectangle,
    PeriodTooltip,
    SubscriptionGrid
} from './subscriptionsComponents';
import AiStudioNoCreditsModalContent from './serviceCreditsAiStudioModal';

export interface ServiceConfig {
    serviceName: ServicesWithCredits;
    addonKey: string;
    displayName: string;
    description: React.ReactNode;
    learnMoreUrl: string;
    addonCurrencyCode: string;
    disabled?: boolean;
    disabledMessage?: string;
    hasTrialCredits?: boolean;
    isToolSupportedByPlan?: boolean;
    notAvailableInTrial?: boolean;
    // Texts used only by the 'addon' variant. They let the card describe the purchased unit
    // (e.g. 'test orders'), the call-to-action and the purchase confirmation without hardcoding.
    addonCtaLabel?: string;
    addonPackageUnitLabel?: string;
    addonPurchaseSuccessMessage?: React.ReactNode;
}

interface ServiceCreditsProps {
    services: ServiceConfig[];
    subscriptionCurrencyCode: string;
    isShopify?: boolean;
    multiservice?: boolean;
    onZeroCredits?: () => void;
    onCreditsRestored?: () => void;
    onUpgradePlanCompleted?: () => void;
    enableUpgradeDialog?: boolean;
    marketPlace?: MarketPlaceProviders;
    // 'credits' (default) renders the full credit-management layout (counter, package selector, auto-refill).
    // 'addon' renders the non-credit add-on card layout and purchases the first available package on activation.
    // 'aiStudioModal' renders the bespoke product-setup "You're out of AI credits" layout (purple header, chips,
    // feature list) while reusing the same balance/package/purchase logic.
    variant?: 'credits' | 'addon' | 'aiStudioModal';
    // Fired after a successful purchase that did not require an external approval redirect.
    onPurchaseCompleted?: () => void;
    // Base URL the Shopify approval flow returns to (defaults to the subscriptions page). The
    // `for<SERVICE>=true` marker is appended automatically so the return can be detected and finalized.
    returnUrlAfterApproval?: string;
}

const ServiceCreditsNoPermission: FC<ServiceCreditsProps> = (props) => {
    const { services } = props;
    return (
        <>
            <ServiceCreditsSectionTitle>
                <strong>{T._('Service Credits', 'Admin')}</strong>
            </ServiceCreditsSectionTitle>
            <ServiceCreditsContainer>
                <NotificationBanner type={'WARNING'} content={T._('You do not have permission to access this section.', 'Admin')} />
            </ServiceCreditsContainer>
        </>
    );
};

const ServiceCredits: FC<ServiceCreditsProps> = (props) => {
    const {
        services,
        subscriptionCurrencyCode = 'USD',
        isShopify = false,
        multiservice = true,
        onZeroCredits,
        onCreditsRestored,
        onUpgradePlanCompleted,
        enableUpgradeDialog = false,
        marketPlace,
        variant = 'credits',
        onPurchaseCompleted,
        returnUrlAfterApproval
    } = props;
    const { showDialog } = useDialogManager();
    const { dispatch } = useAppState();

    // GraphQL Queries - Initial query for all services
    const serviceCreditsData = useLazyLoadQuery<serviceCreditsQuery>(
        graphql`
            query serviceCreditsQuery($serviceName: ServicesWithCredits!, $addonKey: String!, $currencyCode: String!, $baseCurrencyCode: String!) {
                ...serviceCreditsServiceCredit_fragment @arguments(serviceName: $serviceName)
                ...serviceCreditsAddon_fragment @arguments(addonKey: $addonKey, currencyCode: $currencyCode)
                exchangeRates(baseCurrencyCode: $baseCurrencyCode) {
                    baseCurrency
                    rates {
                        currencyID
                        code
                        symbol
                        rate
                        zeroDecimal
                    }
                }
            }
        `,
        {
            serviceName: services[0].serviceName,
            addonKey: services[0].addonKey,
            currencyCode: services[0].addonCurrencyCode,
            baseCurrencyCode: services[0].addonCurrencyCode ?? 'USD'
        },
        { fetchPolicy: 'store-or-network' }
    );

    const [queryServiceCredit, refetchServiceCredit] = useRefetchableFragment<
        serviceCredits_fragmentServiceCreditQuery,
        serviceCreditsServiceCredit_fragment$key
    >(
        graphql`
            fragment serviceCreditsServiceCredit_fragment on Query
            @refetchable(queryName: "serviceCredits_fragmentServiceCreditQuery")
            @argumentDefinitions(serviceName: { type: "ServicesWithCredits!" }) {
                serviceCredit(serviceName: $serviceName) {
                    serviceCreditID
                    service
                    credit
                    autoRefill
                    unlimitedCredits
                    trialCreditsUsed
                    hasSellerPurchasedCredits
                }
            }
        `,
        serviceCreditsData
    );

    const [queryAddon, refetchAddon] = useRefetchableFragment<any, any>(
        graphql`
            fragment serviceCreditsAddon_fragment on Query
            @refetchable(queryName: "serviceCredits_fragmentAddonQuery")
            @argumentDefinitions(addonKey: { type: "String!" }, currencyCode: { type: "String!" }) {
                addon(addonKey: $addonKey, currencyCode: $currencyCode) {
                    addonID
                    name
                    rates {
                        feeValue
                        usageFrom
                        rateID
                    }
                }
            }
        `,
        serviceCreditsData
    );

    // GraphQL Mutations
    const [buyCreditsForService] = useMutation<serviceCreditsBuyServiceCreditsMutation>(graphql`
        mutation serviceCreditsBuyServiceCreditsMutation($input: ServiceRechargeCreditsInput!) {
            buyServiceCredits(input: $input) {
                errorMessage
                result
                urlForApproval
            }
        }
    `);

    const [buyTestOrders] = useMutation<serviceCreditsBuyTestOrdersMutation>(graphql`
        mutation serviceCreditsBuyTestOrdersMutation($input: ServiceRechargeCreditsInput!) {
            buyTestOrders(input: $input) {
                errorMessage
                result
                urlForApproval
            }
        }
    `);

    const [toggleAutoRefill] = useMutation(graphql`
        mutation serviceCreditsToggleAutoRefillMutation($serviceName: ServicesWithCredits!, $isActive: Boolean!) {
            toggleAutoRefill(serviceName: $serviceName, isActive: $isActive)
        }
    `);

    const updateServiceCredits = (service: ServicesWithCredits, addonKey: string, currencyCode: string) => {
        startTransition(() => {
            refetchServiceCredit({ serviceName: service }, { fetchPolicy: 'network-only' });
            refetchAddon(
                {
                    addonKey,
                    currencyCode
                },
                { fetchPolicy: 'network-only' }
            );
            setIsLoadingNewService(false);
        });
    };

    // Query string parameters
    const querystringParams = useQueryParams();

    // State
    const [selectedServiceName, setSelectedServiceName] = useState<ServicesWithCredits>(services[0].serviceName);
    const [selectedTier, setSelectedTier] = useState<number | null>(null);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [autoRefillEnabled, setAutoRefillEnabled] = useState<boolean>(false);

    const [isLoadingNewService, setIsLoadingNewService] = useState<boolean>(false);

    // Constants
    const currentService = services.find((s) => s.serviceName === selectedServiceName) || services[0];
    const serviceName = currentService.serviceName;
    const addonKey = currentService.addonKey;
    const addonCurrencyCode = currentService.addonCurrencyCode || 'USD';
    const disabled = currentService.disabled || false || queryServiceCredit.serviceCredit?.unlimitedCredits === true;
    const disabledMessage = currentService.disabledMessage;
    const notAvailableInTrial = currentService.notAvailableInTrial || false;
    const displayName = currentService.displayName;
    const description = currentService.description;
    const learnMoreUrl = currentService.learnMoreUrl;
    const hasTrialCredits = currentService.hasTrialCredits ?? false;
    const isToolSupportedByPlan = currentService.isToolSupportedByPlan ?? true;
    const needsUpgrade = enableUpgradeDialog && !isToolSupportedByPlan;
    const serviceCredit = queryServiceCredit.serviceCredit;
    const addon = queryAddon.addon;
    const serviceLabelName = displayName || addon?.name || serviceName;
    const serviceDescription = description || T._('Purchase credits to use this service. Credits will be deducted as you use the service.', 'Admin');
    const currencyRateForPrice =
        subscriptionCurrencyCode === 'EUR' || subscriptionCurrencyCode === 'USD' || subscriptionCurrencyCode === 'GBP'
            ? 1
            : (serviceCreditsData.exchangeRates.rates?.find((rate) => rate?.code === subscriptionCurrencyCode)?.rate ?? 1);

    const hasCurrencyDecimals = !serviceCreditsData.exchangeRates.rates?.find((rate) => rate?.code === subscriptionCurrencyCode)?.zeroDecimal;

    const formatPrice = (value: number) =>
        `${(value * currencyRateForPrice).toFixed(hasCurrencyDecimals ? 2 : 0).replace('.', ',')} ${subscriptionCurrencyCode}`;

    // Build credit package options. From the second package onwards we compute a
    // discount based on the first package's per-credit price and show the full,
    // non-discounted price crossed out alongside the discount percentage.
    const addonRates = (addon?.rates ?? []) as Array<{ rateID: number; usageFrom: number; feeValue: number }>;
    const baseUnitPrice = addonRates.length > 0 && addonRates[0].usageFrom > 0 ? addonRates[0].feeValue / addonRates[0].usageFrom : 0;
    const packageOptions = addonRates.map((rate, index) => {
        const fullPrice = baseUnitPrice * rate.usageFrom;
        const discountPerc = index > 0 && fullPrice > rate.feeValue ? Math.round((1 - rate.feeValue / fullPrice) * 100) : 0;
        return {
            value: rate.rateID,
            label: `${rate.usageFrom} ${T._('credits', 'Admin')} - ${formatPrice(rate.feeValue)}`,
            usageFrom: rate.usageFrom,
            price: formatPrice(rate.feeValue),
            originalPrice: discountPerc > 0 ? formatPrice(fullPrice) : null,
            discountPerc
        };
    });
    type PackageOption = (typeof packageOptions)[number];
    const formatPackageOption = (option: PackageOption) => (
        <PackageOptionLabel>
            <span>{stringFormat(T._('{0} credits', 'Admin'), String(option.usageFrom))}</span>
            <PackageOptionPrice>
                {option.originalPrice && <PackageOriginalPrice>{option.originalPrice}</PackageOriginalPrice>}
                <span>{option.price}</span>
                {option.discountPerc > 0 && <PackageDiscountBadge>{stringFormat(T._('-{0}%', 'Admin'), String(option.discountPerc))}</PackageDiscountBadge>}
            </PackageOptionPrice>
        </PackageOptionLabel>
    );
    const successCreditPurchasingMessage = currentService.addonPurchaseSuccessMessage ? (
        <>{currentService.addonPurchaseSuccessMessage}</>
    ) : currentService.serviceName === 'VECTORIZE' ? (
        <>
            {stringFormat(T._('{0} credits added. This feature works through the Printing Method.', 'Admin'), serviceLabelName)}{' '}
            <LinkSuccessPurchasingCreditsMessage href={learnMoreUrl} target='_blank'>
                {T._('Open the guide', 'Admin')}
            </LinkSuccessPurchasingCreditsMessage>{' '}
            {T._('to learn how it works.', 'Admin')}
        </>
    ) : currentService.serviceName === 'GENERATIVE_AI' ? (
        <>
            {stringFormat(T._('{0} credits added. You can now use AI Creative Tools.', 'Admin'), serviceLabelName)}{' '}
            <LinkSuccessPurchasingCreditsMessage href={learnMoreUrl} target='_blank'>
                {T._('Open the guide', 'Admin')}
            </LinkSuccessPurchasingCreditsMessage>{' '}
            {T._('to learn how it works.', 'Admin')}
        </>
    ) : currentService.serviceName === 'AI_STUDIO' ? (
        <>
            {stringFormat(T._('{0} credits added. You can now use AI Studio.', 'Admin'), serviceLabelName)}{' '}
            <LinkSuccessPurchasingCreditsMessage href={learnMoreUrl} target='_blank'>
                {T._('Open the guide', 'Admin')}
            </LinkSuccessPurchasingCreditsMessage>{' '}
            {T._('to learn how it works.', 'Admin')}
        </>
    ) : (
        <>{stringFormat(T._('{0} credits added.', 'Admin'), serviceLabelName)}</>
    );

    useEffect(() => {
        let url = new URL(window.location.href);

        // Handle redirect after upgrading plan and purchasing credits for Shopify
        if (
            isShopify &&
            url.searchParams.get('selectedPackage') &&
            url.searchParams.get('forPurchasingAfterUpgrade') === 'true' &&
            url.searchParams.get('selectedService') &&
            (addon?.rates ?? []).length > 0
        ) {
            let selectedPackage = parseInt(querystringParams.get('selectedPackage') || '');
            let selectedServiceFromUrl = querystringParams.get('selectedService')?.toUpperCase();
            let selectedService = services.find((s) => s.serviceName === selectedServiceFromUrl) as ServicesWithCredits | undefined;

            if (selectedService && selectedService != undefined) {
                setSelectedServiceName(selectedService);
                updateServiceCredits(selectedServiceName, currentService.addonKey, currentService.addonCurrencyCode);

                if (selectedPackage > 0 && addon.rates.some((r: any) => r.rateID === selectedPackage)) setSelectedTier(selectedPackage);
                else setSelectedTier(addon.rates[0].rateID);

                if (!disabled) {
                    dispatch(showLoadingAction(true));
                    handleBuyCredits(false, null, selectedPackage);
                }

                return;
            }
        }

        // Handle Shopify redirect after approval
        if (url.searchParams.get('charge_id') && url.searchParams.get(`for${serviceName}`) && isShopify) {
            // After returning from Shopify approval for purchasing credits
            handleBuyCredits(false, querystringParams.get('charge_id'));

            // Remove the query parameters from the URL
            url.searchParams.delete('charge_id');
            url.searchParams.delete(`for${serviceName}`);
            window.history.replaceState({}, document.title, url.toString());
            if (multiservice) setSelectedServiceName(services.find((s) => s.serviceName === serviceName)?.serviceName || services[0].serviceName);
        }
    }, []);

    // Sync auto-refill state with service credit data
    useEffect(() => {
        if (serviceCredit && serviceCredit.autoRefill !== null) {
            setAutoRefillEnabled(serviceCredit.autoRefill);
        }
    }, [serviceCredit]);

    // Notify parent when credits are zero (blink) or restored (stop blinking)
    useEffect(() => {
        if (serviceCredit === undefined) return;
        if (serviceCredit !== null && (serviceCredit.credit ?? 0) === 0 && serviceCredit.unlimitedCredits !== true) {
            onZeroCredits?.();
        } else {
            onCreditsRestored?.();
            window.dispatchEvent(new CustomEvent('creditsRestored'));
        }
    }, [serviceCredit]);

    // Reset selected tier when service changes
    useEffect(() => {
        if (multiservice) {
            setSelectedTier(null);
            updateServiceCredits(selectedServiceName, currentService.addonKey, currentService.addonCurrencyCode);
        }
    }, [selectedServiceName]);

    // Product-setup modal ('aiStudioModal'): pre-select the first (cheapest) package so the purchase CTA is
    // actionable on open. Scoped to this variant so the other layouts keep their empty default.
    useEffect(() => {
        if (variant !== 'aiStudioModal' || selectedTier !== null) return;
        const firstRateId = addon?.rates?.[0]?.rateID;
        if (firstRateId != null) setSelectedTier(firstRateId);
    }, [variant, selectedTier, addon]);

    // Handlers
    const handleBuyCredits = async (isTrial: boolean, shopifyChargeID: string | null = null, tier: number | null = null) => {
        const url = new URL(window.location.href);
        // Where Shopify returns to after approving the charge. Callers can override the base (e.g. the test
        // product page); we always append the `for<SERVICE>=true` marker so the return can be finalized.
        const returnBase = returnUrlAfterApproval ?? `${url.protocol}//${url.host}${backofficeUrl(`/User/?tab=subscriptions`)}`;
        const redirectUrlFromApproval = `${returnBase}${returnBase.includes('?') ? '&' : '?'}for${serviceName}=true`;

        setIsProcessing(true);
        let creditsToBuy = 0;

        if (!shopifyChargeID && !isTrial) {
            if ((!selectedTier && !tier) || !addon) {
                showNotification({
                    type: 'warning',
                    message: T._('Please select a package before purchasing credits.', 'Admin'),
                    dismiss: {
                        duration: 2000
                    }
                });
                setIsProcessing(false);
                return;
            }

            const selectedRate = tier ? addon.rates.find((r: any) => r.rateID === tier) : addon.rates.find((r: any) => r.rateID === selectedTier);

            if (!selectedRate) {
                showNotification({
                    type: 'danger',
                    message: T._('Invalid package selected.', 'Admin'),
                    dismiss: {
                        duration: 2000
                    }
                });
                setIsProcessing(false);
                return;
            }
            creditsToBuy = selectedRate.usageFrom;
        }

        // Test orders use a dedicated mutation (buyTestOrders) but share the same input and result shape.
        const isTestOrders = serviceName === 'TEST_ORDERS';

        const onCompleted = (response: serviceCreditsBuyServiceCreditsMutation$data | serviceCreditsBuyTestOrdersMutation$data) => {
            const operationResult = isTestOrders
                ? (response as serviceCreditsBuyTestOrdersMutation$data).buyTestOrders
                : (response as serviceCreditsBuyServiceCreditsMutation$data).buyServiceCredits;
            if (operationResult && operationResult.result !== 'FAILED') {
                if (operationResult.urlForApproval) {
                    // Redirect to approval URL (for Shopify)
                    window.location.href = operationResult.urlForApproval;
                } else {
                    setIsProcessing(false);
                    showNotification({
                        type: 'success',
                        message: isTrial ? T._('Trial credits have been successfully added to your account.', 'Admin') : successCreditPurchasingMessage,
                        dismiss: {
                            duration: 10000
                        }
                    });
                    updateServiceCredits(currentService.serviceName, currentService.addonKey, currentService.addonCurrencyCode);
                    onPurchaseCompleted?.();
                }
            } else {
                setIsProcessing(false);
                showNotification({
                    type: 'danger',
                    message: operationResult?.errorMessage || T._('An error occurred while processing your request.', 'Admin'),
                    dismiss: {
                        duration: 3000
                    }
                });
            }
        };

        const onError = () => {
            setIsProcessing(false);
            showNotification({
                type: 'danger',
                message: T._('An error occurred while purchasing credits.', 'Admin'),
                dismiss: {
                    duration: 2000
                }
            });
        };

        const variables = {
            input: {
                service: serviceName,
                shopifyChargeID: shopifyChargeID,
                credits: creditsToBuy,
                autoRefill: true,
                currencyCode: addonCurrencyCode,
                isForTrial: isTrial,
                returnUrlFromApproval: isShopify ? redirectUrlFromApproval : null
            }
        };

        if (isTestOrders) {
            buyTestOrders({ onCompleted, onError, variables });
        } else {
            buyCreditsForService({ onCompleted, onError, variables });
        }
    };

    const handleAutoRefillChange = (checked: boolean) => {
        toggleAutoRefill({
            onCompleted: () => {
                setAutoRefillEnabled(checked);
                updateServiceCredits(currentService.serviceName, currentService.addonKey, currentService.addonCurrencyCode);
            },
            onError: (error) => {
                showNotification({
                    type: 'danger',
                    message: T._('Failed to update auto-refill setting.', 'Admin'),
                    dismiss: {
                        duration: 2000
                    }
                });
            },
            variables: {
                serviceName: serviceName,
                isActive: checked
            }
        });
    };

    const handlePurchaseWithUpgrade = () => {
        if (!needsUpgrade) {
            handleBuyCredits(false, null);
            return;
        }

        if (!selectedTier || !addon) {
            showNotification({
                type: 'warning',
                message: T._('Please select a package before purchasing credits.', 'Admin'),
                dismiss: {
                    duration: 2000
                }
            });
            return;
        }

        showDialog(
            'service-credits-upgrade',
            <UpgradeDialog
                type='DIALOG'
                message={T._(
                    'To use this feature you need to upgrade your plan. After upgrading, your selected credit package will be purchased automatically.',
                    'Admin'
                )}
                buttonUpgradeText={T._('Upgrade & Purchase', 'Admin')}
                marketPlace={marketPlace}
                redirectUrlFromDashboard={encodeURI(
                    window.location.origin +
                        backofficeUrl(`/User/?tab=subscriptions`) +
                        `&forPurchasingAfterUpgrade=true&selectedService=${serviceName}&selectedPackage=${selectedTier}`
                )}
                onUpgradeCompleted={async () => {
                    onUpgradePlanCompleted?.();
                    handleBuyCredits(false, null);
                }}
            />
        );
    };

    const hasCredits = serviceCredit !== null && ((serviceCredit.credit ?? 0) > 0 || serviceCredit.unlimitedCredits === true);

    // Add-on card layout: reuses the credit purchasing flow but renders the non-credit add-on card
    // (no credit counter, package selector, auto-refill or no-credits message). Activation purchases
    // the first available package.
    if (variant === 'addon') {
        const firstPackage = addonRates[0];
        const tooltipMessage = disabled ? disabledMessage || T._('This action is disabled.', 'Admin') : undefined;

        return (
            <BorderedRectangle>
                <SubscriptionGrid>
                    <AddOnHeader>
                        <label>{serviceLabelName}</label>
                    </AddOnHeader>
                    <AddOnDescription>
                        {serviceDescription}{' '}
                        {learnMoreUrl && (
                            <Link href={learnMoreUrl} target='_blank'>
                                {T._('Learn more', 'Admin')}
                            </Link>
                        )}
                    </AddOnDescription>
                    {firstPackage && (
                        <AddPriceSection>
                            <PeriodTooltip>
                                <label>
                                    <strong>{formatPrice(firstPackage.feeValue)}</strong>
                                </label>
                                {currentService.addonPackageUnitLabel && (
                                    <label>{' / ' + firstPackage.usageFrom + ' ' + currentService.addonPackageUnitLabel}</label>
                                )}
                            </PeriodTooltip>
                        </AddPriceSection>
                    )}
                    <ActivateRemoveContainer>
                        {isProcessing ? (
                            <Center>
                                <Spinner />
                            </Center>
                        ) : (
                            <ZTooltip title={tooltipMessage} position={'top'}>
                                <PrimaryButtonWrapper>
                                    <PrimaryButton
                                        $size='small'
                                        disabled={isProcessing || disabled || !firstPackage}
                                        onClick={() => handleBuyCredits(false, null, firstPackage?.rateID ?? null)}
                                    >
                                        {currentService.addonCtaLabel ?? T._('Buy', 'Admin')}
                                    </PrimaryButton>
                                </PrimaryButtonWrapper>
                            </ZTooltip>
                        )}
                    </ActivateRemoveContainer>
                </SubscriptionGrid>
            </BorderedRectangle>
        );
    }

    // Product-setup "You're out of AI credits" layout. Reuses every value/handler computed above and only
    // swaps the presentation (marketing header + feature list + gradient CTA). Used by the side-images dialog.
    if (variant === 'aiStudioModal') {
        const creditsAvailableLabel =
            serviceCredit?.unlimitedCredits === true
                ? T._('Unlimited', 'Admin')
                : (serviceCredit?.credit ?? 0) === 0
                  ? '0'
                  : (serviceCredit?.credit ?? 0).toFixed(2).replace('.', ',');
        const startingAtLabel = addonRates.length > 0 ? formatPrice(addonRates[0].feeValue) : null;

        return (
            <AiStudioNoCreditsModalContent
                creditsAvailableLabel={creditsAvailableLabel}
                packageOptions={packageOptions}
                selectedTier={selectedTier}
                onSelectTier={setSelectedTier}
                formatPackageOption={formatPackageOption}
                hasPackages={packageOptions.length > 0}
                autoRefillEnabled={autoRefillEnabled}
                onToggleAutoRefill={handleAutoRefillChange}
                onBuy={handlePurchaseWithUpgrade}
                isProcessing={isProcessing}
                disabled={disabled}
                startingAtLabel={startingAtLabel}
                learnMoreUrl={learnMoreUrl}
            />
        );
    }

    return (
        <>
            {!notAvailableInTrial && (
                <BadgeStatusLabel $active={hasCredits}>{hasCredits ? T._('Activated', 'Admin') : T._('Not activated', 'Admin')}</BadgeStatusLabel>
            )}
            {notAvailableInTrial && (
                <TrialBadgeWrapper>
                    <OnlyPaidPlans />
                </TrialBadgeWrapper>
            )}
            <ServiceCreditsSectionTitle>
                <strong>{serviceLabelName}</strong>
                <p>
                    {serviceDescription}
                    <br />
                    <span style={{ fontWeight: 500, color: '#263238' }}>
                        {stringFormat(
                            T._('Starts at {0}, credits required.', 'Admin'),
                            ((addon?.rates[0].feeValue ?? 0) * currencyRateForPrice).toFixed(hasCurrencyDecimals ? 2 : 0).replace('.', ',') +
                                ' ' +
                                subscriptionCurrencyCode
                        )}
                    </span>{' '}
                    {learnMoreUrl && (
                        <Link href={learnMoreUrl} target='_blank'>
                            {T._('Learn more', 'Admin')}
                        </Link>
                    )}
                </p>
            </ServiceCreditsSectionTitle>

            <ServiceCreditsContainer>
                {(isProcessing || isLoadingNewService) && (
                    <LoadingOverlay>
                        <Loader />
                    </LoadingOverlay>
                )}
                <ServiceCreditsContent>
                    {/* Display current credits */}
                    <CreditsInfoSection>
                        <CreditsLabel>
                            <strong>
                                {serviceCredit?.unlimitedCredits === true
                                    ? 'Unlimited'
                                    : (serviceCredit?.credit ?? 0) == 0
                                      ? 0
                                      : (serviceCredit?.credit ?? 0).toFixed(2).replace('.', ',')}
                            </strong>
                            <label>{T._('Available credits', 'Admin')}</label>
                        </CreditsLabel>
                        <CreditsWarning
                            aria-hidden={!(!notAvailableInTrial && (serviceCredit?.credit ?? 0) == 0)}
                            $hidden={!(!notAvailableInTrial && (serviceCredit?.credit ?? 0) == 0)}
                        >
                            {T._("You don't have any credits. Please purchase credits to use this feature.", 'Admin')}
                        </CreditsWarning>
                    </CreditsInfoSection>
                    {/* Service and Tier Selection */}
                    <SelectorsRow>
                        {multiservice && (
                            <TierSelectionSection>
                                <label>{T._('Select service', 'Admin')}</label>
                                <AdvancedSelect
                                    isSearchable={false}
                                    placeholder={T._('Select a service...', 'Admin')}
                                    options={services.map((s) => ({
                                        value: s.serviceName,
                                        label: s.displayName
                                    }))}
                                    value={{
                                        value: selectedServiceName,
                                        label: currentService.displayName
                                    }}
                                    onChange={(val) => {
                                        if (val?.value) {
                                            setSelectedServiceName(val.value as ServicesWithCredits);
                                        }
                                    }}
                                />
                            </TierSelectionSection>
                        )}
                        <TierSelectionSection>
                            <label>{T._('Select Credit Package', 'Admin')}</label>
                            <AdvancedSelect
                                isSearchable={false}
                                menuPosition={'fixed'}
                                menuPlacement={'auto'}
                                isDisabled={isProcessing || (disabled && !needsUpgrade)}
                                options={packageOptions}
                                value={packageOptions.find((option) => option.value === selectedTier) || null}
                                formatOptionLabel={formatPackageOption}
                                onChange={(val) => {
                                    setSelectedTier(val?.value ?? null);
                                }}
                                placeholder={T._('Select a package...', 'Admin')}
                            />
                        </TierSelectionSection>
                    </SelectorsRow>
                    {/* Auto-refill Option */}
                    <AutoRefillSection>
                        <Checkbox
                            checked={autoRefillEnabled}
                            onChange={handleAutoRefillChange}
                            label={
                                isShopify
                                    ? T._("You'll get email alerts when your credits drop below 20% and when they run out, so you can top up.", 'Admin')
                                    : T._('Enable auto-refill', 'Admin')
                            }
                            caption={
                                isShopify
                                    ? null
                                    : T._(
                                          'Automatically purchase the same credit package when your balance drops to 10% to avoid service interruption',
                                          'Admin'
                                      )
                            }
                            disabled={isProcessing || disabled}
                        />
                    </AutoRefillSection>
                    {(!addon || !addon.rates || addon.rates.length === 0) && (
                        <NotificationBanner type='WARNING' content={T._('No credit packages are currently available for this service.', 'Admin')} />
                    )}
                </ServiceCreditsContent>
            </ServiceCreditsContainer>
            {/* Action Buttons */}
            <ActionsSection>
                {hasTrialCredits && !serviceCredit?.trialCreditsUsed && (
                    <ZTooltip title={disabled ? disabledMessage || T._('This action is disabled.', 'Admin') : undefined} position={'top'}>
                        <PrimaryButtonWrapper>
                            <ActivateTrialButton
                                $size='small'
                                disabled={isProcessing || (disabled && config.isProduction)}
                                onClick={() => handleBuyCredits(true, null)}
                            >
                                {T._('Get Trial Credits', 'Admin')}
                            </ActivateTrialButton>
                        </PrimaryButtonWrapper>
                    </ZTooltip>
                )}
                <ZTooltip title={disabled && !needsUpgrade ? disabledMessage || T._('This action is disabled.', 'Admin') : undefined} position={'top'}>
                    <PrimaryButtonWrapper>
                        <TextButton disabled={!selectedTier || isProcessing || (disabled && !needsUpgrade)} onClick={handlePurchaseWithUpgrade}>
                            {T._('Purchase Credits', 'Admin')}
                        </TextButton>
                    </PrimaryButtonWrapper>
                </ZTooltip>
            </ActionsSection>
        </>
    );
};

// Wrapper component with permission check
const ServiceCreditsWithPermission: FC<ServiceCreditsProps> = (props) => (
    <IfCanAccess permission='SUBSCRIPTION' fallback={<ServiceCreditsNoPermission {...props} />}>
        <ServiceCredits {...props} />
    </IfCanAccess>
);

// Deferring mount until after commit ensures Relay runs in a stable render context.
const ServiceCreditsDeferred: FC<ServiceCreditsProps> = (props) => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
    }, []);
    if (!mounted) return null;
    return <ServiceCreditsWithPermission {...props} />;
};

export default ServiceCreditsDeferred;
